import { cn } from '@client/lib';
import { Plus } from 'lucide-react';

export type EmptyStateAddOptionDef = {
  title: string;
  description: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  onClick: () => void;
  poweredBy?: string;
  testId?: string;
};

export default function EmptyStateAddButtons({
  className,
  addOptions,
  title,
  description,
  forModal,
}: {
  className?: string;
  title?: string;
  description?: string;
  addOptions: EmptyStateAddOptionDef[];
  forModal?: boolean;
}) {
  return (
    <div className={className}>
      {title && <h2 className="text-lg font-medium leading-6 text-gray-900 md:text-xl">{title}</h2>}
      {description && <p className="mt-1 text-base text-gray-700">{description}</p>}
      <ul
        className={`mt-6 grid grid-cols-1 gap-6 border-t border-gray-200 py-6 ${forModal ? '' : ' sm:grid-cols-3'}`}
      >
        {addOptions.map((item, itemIdx) => {
          // Prefer explicit test id if provided; otherwise derive from title
          const testId =
            item.testId ??
            `empty-state-${item.title.toLowerCase().replace(/\s+/g, '-').replace(/\//g, '-')}`;

          return (
            <li
              key={itemIdx}
              className={cn(
                'border-primary-200 bg-primary-50 hover:bg-primary-100 relative flow-root cursor-pointer rounded-2xl border p-5',
                {
                  'pb-10': item.poweredBy,
                },
              )}
              onClick={item.onClick}
              data-testid={testId}
            >
              <div className="focus-within:ring-primary relative flex flex-col gap-2 rounded-xl focus-within:ring-1">
                {/* Icon and plus button*/}
                <div className="flex items-center justify-between">
                  <item.icon
                    aria-hidden="true"
                    className="text-primary-200 h-10 w-10 md:h-12 md:w-12"
                  />
                  <div className="bg-primary hover:bg-primary-hover flex h-9 w-9 items-center justify-center rounded-full p-2">
                    <Plus
                      aria-hidden="true"
                      className="h-5 w-5 text-white"
                    />
                  </div>
                </div>
                {/* Title and description */}
                <div>
                  <h3 className="text-primary-dark text-sm font-medium focus:outline-none">
                    <span
                      aria-hidden="true"
                      className="absolute inset-0"
                    />
                    <span className="text-sm">{item.title}</span>
                  </h3>
                  <p className="mt-1 text-sm text-gray-700 ">{item.description}</p>
                </div>
              </div>
              {item?.poweredBy && (
                <p className="text-primary/60 absolute bottom-2 right-3 text-xs italic">
                  {item?.poweredBy}
                </p>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
